import { isErrorPolyfill } from '@/shared/utils/errors';
import type { Client as BugsnagClient, Event, NotifiableError } from '@bugsnag/js';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import type { Plugin } from 'vue';
import { IS_LOCAL_ENV } from './environment';

const NO_OP = () => {};

export const startBugsnag = (): BugsnagClient => {
    if (IS_LOCAL_ENV || Bugsnag.isStarted()) {
        return Bugsnag;
    }

    Bugsnag.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        releaseStage: import.meta.env.VITE_BUGSNAG_RELEASE_STAGE,
    });

    window.Bugsnag = Bugsnag;
    return Bugsnag;
};

export const getBugsnagVuePlugin = (): Plugin => {
    if (IS_LOCAL_ENV) {
        return NO_OP;
    }

    if (!Bugsnag.isStarted()) {
        startBugsnag();
    }

    return Bugsnag.getPlugin('vue')!;
};

type Severity = Event['severity'];
type Context = Event['context'];
type Metadata = Record<string, unknown>;
type Notification = NotifiableError | unknown;

/** Private wrapper to abstract away Bugsnag details */
const notify = (notification: Notification, severity: Severity, context: Context, metadata?: Metadata) => {
    const METADATA_SECTION = 'custom';
    if (IS_LOCAL_ENV) {
        const consoleMethod = severity === 'error' ? 'error' : 'warn';
        console[consoleMethod](`[BUGSNAG - local env - ${context}]:`, notification, metadata);
        return;
    }

    const transformedNotification = makeNotifiable(notification, severity);

    startBugsnag().notify(transformedNotification, event => {
        event.severity = severity;
        event.context = context;
        event.addMetadata(METADATA_SECTION, 'context', context);
        if (metadata) {
            for (const [key, value] of Object.entries(metadata)) {
                event.addMetadata(METADATA_SECTION, key, value);
            }
        }
    });
};

/** Checks if error/message qualifies as Bugsnag's NotifiableError */
const isNotifiable = (error: unknown): error is NotifiableError => {
    if (typeof error === 'string') return true;
    if (!error || typeof error !== 'object') return false;

    const isErrorInstance = isErrorPolyfill(error);
    const hasExpectedErrorClassProps = 'errorClass' in error && 'errorMessage' in error;
    const hasExpectedErrorMessageProps = 'name' in error && 'message' in error;

    return isErrorInstance || hasExpectedErrorClassProps || hasExpectedErrorMessageProps;
};

const makeNotifiable = (notification: string | unknown, severity: Severity): NotifiableError => {
    if (isNotifiable(notification)) {
        return notification;
    }
    if (severity === 'error') {
        // make it a proper error if severity indicates that
        return new Error(typeof notification === 'string' ? notification : 'Unknown error');
    }
    return { errorClass: severity.toUpperCase(), errorMessage: `${notification}` };
};

export const notifyWarning = (error: Notification, context?: Context, metadata?: Metadata) => {
    notify(error, 'warning', context, metadata);
};

export const notifyError = (error: Notification, context?: Context, metadata?: Metadata) => {
    notify(error, 'error', context, metadata);
};
